import { Heading, Text } from '@trmediaab/zebra';
import type { Order, Product } from '@trmediaab/zebra-types/trhub';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

import Layout from '../../components/Layout';
import NextButton from '../../components/NextButton';
import ProgressBar from '../../components/ProgressBar';
import {
  setError,
  setOrder,
  setSelectedSku,
  useImmutableState,
} from '../../state';
import { ensureErrorWithMessage } from '../../utils/ensureErrorWithMessage';
import request from '../../utils/request';
import ProductCard from './ProductCard';

interface ProductButtonProps {
  sku: string;
  headline?: string;
}

const ProductButton = ({ sku, headline }: ProductButtonProps) => {
  const { selectedSku } = useImmutableState();

  const selected = sku === selectedSku;

  const selectProduct = useCallback(() => {
    setSelectedSku(sku);
  }, [sku]);

  return (
    <ProductCard
      sku={sku}
      headline={headline}
      selected={selected}
      onClick={selectProduct}
    />
  );
};

interface ProductProps {
  sku: string;
  onNext: () => void;
}

const ProductView = ({ sku, onNext }: ProductProps) => {
  const { selectedSku, hasProductOptions } = useImmutableState();
  const [submitting, setSubmitting] = useState(false);
  const { data } = useSWR<Product>(`product/${sku}`);

  useEffect(() => {
    if (selectedSku == null) {
      setSelectedSku(sku);
    }
  }, [selectedSku, sku]);

  const paymentOptions =
    data != null &&
    'paymentOptions' in data &&
    Array.isArray(data.paymentOptions)
      ? data.paymentOptions
      : null;

  const initOrder = useCallback(async () => {
    try {
      const data = await request<Order>(`order/product/${selectedSku}`);
      setOrder(data);
      onNext();
    } catch (error) {
      setError(ensureErrorWithMessage(error));
    }
  }, [onNext, selectedSku]);

  // Init order immediately and move to next step if there's no products too choose from
  useEffect(() => {
    if (hasProductOptions === false) {
      initOrder();
    }
  }, [hasProductOptions, initOrder]);

  const onSubmit = useCallback(async () => {
    setSubmitting(true);
    await initOrder();
    setSubmitting(false);
  }, [initOrder]);

  const layoutAreas = useMemo(
    () =>
      Array.from({
        length: paymentOptions?.length ?? 0,
      }).reduce<{ oneCol: string[]; twoCol: string[] }>(
        (acc, _, idx) => {
          acc.oneCol.push(`p${idx}`);
          if (idx % 2 === 0) {
            acc.twoCol.push(`p${idx} p${idx + 1}`);
          }
          return acc;
        },
        { oneCol: ['header'], twoCol: ['header header'] },
      ),
    [paymentOptions],
  );

  return (
    <Layout areas={layoutAreas}>
      <ProgressBar progress={data != null ? 25 : 0} />

      {paymentOptions != null && paymentOptions.length > 1 && (
        <>
          <>
            <Layout.area key={sku} area="header">
              <Text textAlign="center">
                <Heading.h4 variant="checkout">Hur vill du betala?</Heading.h4>
                <Text mt={[1, null, 2]}>Välj mellan månad eller helår</Text>
              </Text>
            </Layout.area>

            {paymentOptions.map(({ sku, text }, idx) => (
              <Layout.area key={sku} area={`p${idx}`}>
                <ProductButton sku={sku} headline={text} />
              </Layout.area>
            ))}
          </>

          <Layout.nextStepArea>
            <NextButton
              onClick={onSubmit}
              disabled={data == null}
              showSpinner={submitting}
            />
          </Layout.nextStepArea>
        </>
      )}
    </Layout>
  );
};

export default ProductView;
